import {
  Component,
  OnInit,
  ViewChild,
  EventEmitter,
  Output,
  Input,
  ViewEncapsulation,
} from '@angular/core';
import { MessageService, SelectItem } from 'primeng';
import { Message } from '../../classes/Message';
import { IJobScreen } from '../../interfaces/job/job';
import { JobsService } from '../../services';
import { SupplierFeedbackService } from '../../services';
import { UserService } from '../../services';

@Component({
  selector: 'new-supplier-feedback',
  templateUrl: './new-supplier-feedback.component.html',
  encapsulation: ViewEncapsulation.None,
})
export class NewSupplierFeedbackComponent implements OnInit {
  public isShowCreateFeedback: boolean = false;
  public isBusySubmit: boolean = false;
  public isBusyLoadJob: boolean = false;
  public isBusyLoadUser: boolean = false;
  public isBusyLoadTypes: boolean = false;

  public jobNumberError: boolean = false;
  public loadUserError: boolean = false;
  public loadTypesError: boolean = false;

  public currentUser: any = null;
  public orderId?: number;
  public taskId?: number;

  // set to true when editing existing supplier feedback
  public isEdit: boolean = false;

  // set if editing an existing record
  public typeId?: number;
  public feedbackEditId: number;

  public get currentTime(): Date {
    return new Date();
  }

  public jobNumber: string = '';
  public feedbackMessage: string = '';

  private jobInfo: IJobScreen;
  public jobId: number = 0;

  public types: SelectItem[] = [];
  public selectedType: any = null;

  public orders: SelectItem[] = [];
  public selectedOrder: any = null;

  public tasks: SelectItem[] = [];
  public selectedTask: any = null;

  @Output() feedbackCreated: EventEmitter<any>;

  constructor(
    private jobsService: JobsService,
    private userService: UserService,
    public messageService: MessageService,
    private feedbackService: SupplierFeedbackService,
  ) {
    this.feedbackCreated = new EventEmitter<any>();
  }

  ngOnInit(): void {
    this.isBusyLoadUser = true;
    this.userService
      .getLoggedUser()
      .then((result) => {
        this.isBusyLoadUser = false;
        if (result != null) {
          this.currentUser = result;
        } else {
          this.loadUserError = true;
          this.messageService.add(
            Message.Error(
              "An error occurred while loading the current user's data.",
              'Create Feedback',
            ),
          );
        }
      })
      .catch((ex) => {
        this.isBusyLoadUser = false;
        this.loadUserError = true;
        this.messageService.add(
          Message.Error(
            "An error occurred while loading the current user's data.",
            'Create Feedback',
          ),
        );
      });

    this.isBusyLoadTypes = true;
    this.feedbackService
      .getTypes()
      .then((x) => {
        this.isBusyLoadTypes = false;
        x.filter((x) => x.isActive).forEach((x) => {
          this.types.push({ label: x.description, value: x });
        });
      })
      .catch((ex) => {
        this.isBusyLoadTypes = false;
        this.loadTypesError = true;
        this.messageService.add(
          Message.Error('An error occurred while loading the feedback types.', 'Create Feedback'),
        );
      });
  }

  selectedOrderChanged() {
    //console.log("Selected order changed: ", this.selectedOrder.id);
    this.populateTasks();
  }

  populateTasks() {
    // populate tasks dropdown using selected order
    this.tasks = [];
    let order = this.jobInfo.orders.find((order) => order.id == this.selectedOrder.id);

    if (order) {
      order.tasks.forEach((x) => {
        this.tasks.push({
          label: '[' + x.vehicle.registrationNumber + '] dropoff: ' + x.dropoffLocation.postcode,
          value: x,
        });
      });

      let task = this.tasks.find((x) => x.value.id == this.taskId);
      if (!task && this.tasks.length > 0) this.selectedTask = this.tasks[0].value;
      else this.selectedTask = task.value;
    }
  }

  editFeedback(feedbackId: number, jobInfo: IJobScreen, orderList: any[], orderId?: number) {
    this.isEdit = true;
    this.feedbackEditId = feedbackId;

    // load the feedback record
    this.feedbackService.get(feedbackId).then((res) => {
      this.taskId = res.taskId;
      this.feedbackMessage = res.feedbackHistories[0].text;
      this.typeId = res.supplierFeedbackType.id;

      let type = this.types.find((x) => x.value.id == this.typeId);
      if (type) this.selectedType = type.value;
    });

    this.setData(jobInfo, orderList, orderId);
    this.feedbackMessage;
  }

  createNewFeedback(jobInfo: IJobScreen, orderList: any[], orderId?: number, taskId?: number) {
    this.isEdit = false;
    this.feedbackMessage = '';
    this.taskId = taskId;
    this.selectedType = null;

    this.setData(jobInfo, orderList, orderId);

    let order = this.types.find((x) => x.value.id == orderId);
    if (order != null) {
      this.selectedOrder = order.value;
      this.selectedOrderChanged();
    }
  }

  setData(jobInfo: IJobScreen, orderList: any[], orderId?: number) {
    this.isShowCreateFeedback = true;
    this.orderId = orderId;
    this.jobInfo = jobInfo;

    this.jobId = jobInfo.id;
    this.jobNumber = jobInfo.jobNumber;

    this.selectedOrder = null;
    this.orders = [];

    orderList.forEach((x) => {
      this.orders.push({ label: x.name, value: x });
    });

    if (orderId != null) {
      let order = this.orders.find((x) => x.value.id == orderId);
      if (order != null) {
        this.selectedOrder = order.value;
        this.selectedOrderChanged();
      }
    }
  }

  close() {
    this.isShowCreateFeedback = false;
  }

  submit() {
    //console.log("Adding supplier feedback, selected type: ", this.selectedType, " - id: ", this.selectedType.id);

    this.isBusySubmit = true;

    if (!this.isEdit) {
      this.feedbackService
        .createFeedback({
          jobId: this.jobId,
          orderId: this.selectedOrder.id,
          taskId: this.selectedTask.id,
          message: this.feedbackMessage,
          createDateTime: this.currentTime,
          feedbackTypeId: this.selectedType.id,
        })
        .then((result) => {
          this.isBusySubmit = false;
          this.isShowCreateFeedback = false;
          this.feedbackCreated.emit();
        })
        .catch((ex) => {
          this.isBusySubmit = false;
          this.messageService.add(
            Message.Error(
              'An error occurred while saving the feedback. Please try again or contact your administrator.',
              'Create Supplier Feedback',
            ),
          );
        });
    } else {
      this.feedbackService
        .editFeedback({
          supplierFeedbackId: this.feedbackEditId,
          message: this.feedbackMessage,
          feedbackTypeId: this.selectedType.id,
        })
        .then((result) => {
          this.isBusySubmit = false;
          this.isShowCreateFeedback = false;
          this.feedbackCreated.emit();
        })
        .catch((ex) => {
          this.isBusySubmit = false;
          this.messageService.add(
            Message.Error(
              'An error occurred while saving the feedback. Please try again or contact your administrator.',
              'Edit Supplier Feedback',
            ),
          );
        });
    }
  }
}
